import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { Grid, Box, Flex } from '@chakra-ui/core'
import { PersonalViewItem } from './PersonalViewItem'
import { PersonalViewDrawer } from './PersonalViewDrawer'
import { ChatPreview } from 'components/ChatsPreviews/ChatPreview'
// import { StoreeDollPreview } from 'components/ChatsPreviews/StoreeDollPreview'
import styled from '@emotion/styled'
import { usePersonalViewUsers } from './hooks/usePersonalViewUsers'
import { usePersonalViewChats } from './hooks/usePersonalViewChats'
import { usePersonalViewChatsSorting } from './hooks/usePersonalViewChatsSorting'
import { isRtlSelector } from 'redux/selectors/general'
import { PersonalViewSkeletons } from './PersonalViewSkeleton'
import { PersonalViewFiltersState } from '../PersonalViewFilters'
import { css } from '@emotion/core'
import { Post } from 'redux/reducers/feed'
import { SectionHeader } from './SectionHeader'
import colors from 'constants/colors'
import { Flipper, Flipped } from 'react-flip-toolkit'
import useLocaleText from 'components/useLocaleText'
import { RootState } from 'constants/interfaces'
import { useSetup } from 'contexts/SetupContext'
import { isSetupAllowedForUser, STEPS } from 'utils/setupStorage'
import { useLocation } from 'react-router-dom'
import keys from 'constants/keys'

interface IProps {
  sortedPostsIds: string[]
  filters?: PersonalViewFiltersState
}

const SKELETONS_EXTRA_DELAY = 1500 as const

const MainContentWrapper = styled(Box)`
  flex: 1;
  min-width: 0;
  padding-top: 50px;
  overflow-y: auto;
  overflow-x: hidden;
  height: calc(100vh - 65px);
`

export const PersonalView = ({ sortedPostsIds, filters }: IProps) => {
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [showSkeletonsExtra, setShowSkeletonsExtra] = useState(false)
  const [gridColumns, setGridColumns] = useState(6)

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const isRtl = useSelector(isRtlSelector)
  const appPosts = useSelector((state: RootState) => state.feed.posts)
  const appChats = useSelector((state: RootState) => state.feed.chats)
  const uid = useSelector((state: RootState) => state.auth.uid)

  const statuses = useSelector((state: RootState) => state.tasks?.allTasks?.[0]?.statuses)
  const todoTasksCount = statuses?.[keys.TASK_STATUSES.TODO.value]

  const { sortedUsers, aggregatedByUser, isPostsFetching } = usePersonalViewUsers(sortedPostsIds)
  const { chats, allChatsLoaded, chatsFetching } = usePersonalViewChats()
  const sortedChats = usePersonalViewChatsSorting(chats)

  const t_waiting = useLocaleText('analytical_todo')
  const t_done = useLocaleText('analytical_done')

  const locationPathname = useLocation().pathname

  const { isStepCompleted } = useSetup()
  const isCurrentStepCompleted = isStepCompleted(STEPS.AI_FEED)

  // Apply filters
  const filteredUsers = useMemo(() => {
    if (!filters) return sortedUsers

    return sortedUsers.filter((userItem) => {
      const userData = aggregatedByUser[userItem.userId]
      if (!userData) return false

      const { contentType, status } = filters

      // Filter by content type
      let hasContent = false
      if (contentType === 'all') {
        hasContent = true
      } else if (contentType === 'task') {
        hasContent =
          userData.overdueTasks.length > 0 ||
          userData.urgentTasks.length > 0 ||
          userData.completedTasks.length > 0 ||
          userData.ownerTasks.length > 0
      } else if (contentType === 'post') {
        hasContent = userData.posts.length > 0 || userData.readPosts.length > 0
      } else if (contentType === 'chat') {
        // Users don't have chats, so filter out all users
        return false
      }

      if (!hasContent) return false

      // Filter by status
      if (status === 'all') {
        return true
      } else if (status === 'not_completed') {
        return (
          userData.overdueTasks.length > 0 ||
          userData.urgentTasks.length > 0 ||
          userData.posts.length > 0 ||
          userData.ownerTasks.length > 0
        )
      } else if (status === 'completed') {
        return userData.completedTasks.length > 0 || userData.readPosts.length > 0
      }

      return true
    })
  }, [sortedUsers, aggregatedByUser, filters])

  const filteredChats = useMemo(() => {
    if (!filters) return sortedChats
    const { contentType } = filters
    return contentType === 'all' || contentType === 'chat' ? sortedChats : []
  }, [sortedChats, filters])

  // Combined and sorted items (users + chats)
  const sortedItems = useMemo(() => {
    type CombinedItem =
      | { type: 'user'; userId: string; isFaded: boolean; category: number; sortValue: number }
      | { type: 'chat'; chat: Post; isFaded: boolean; category: number; sortValue: number }

    const items: CombinedItem[] = []

    // Add users - use categories and sortValue from sortedUsers (already sorted)
    filteredUsers.forEach((userItem) => {
      items.push({
        type: 'user',
        userId: userItem.userId,
        isFaded: userItem.isFaded,
        category: userItem.category,
        sortValue: userItem.sortValue,
      })
    })

    // Add chats - use categories and sortValue from sortedChats (already sorted)
    filteredChats.forEach((chatWithMetadata) => {
      items.push({
        type: 'chat',
        chat: chatWithMetadata.chat,
        isFaded: chatWithMetadata.isFaded,
        category: chatWithMetadata.category,
        sortValue: chatWithMetadata.sortValue,
      })
    })

    // Sort by category first, then by sortValue
    return items.sort((a, b) => {
      if (a.category !== b.category) return a.category - b.category

      // Category 1-2: tasks - sort ascending (overdue: most overdue first, active: least time first)
      if (a.category === 1 || a.category === 2) {
        return a.sortValue - b.sortValue
      }

      // Category 3: tasks with > 24h - sort ascending (least time first)
      if (a.category === 3) {
        return a.sortValue - b.sortValue
      }

      // Category 4: unread posts only - sort descending (newest first)
      if (a.category === 4) {
        return b.sortValue - a.sortValue
      }

      // Category 5: unread chats - sort descending (newest first)
      if (a.category === 5) {
        return b.sortValue - a.sortValue
      }

      // Category 6: completed/read - sort descending (latest first)
      if (a.category === 6) {
        return b.sortValue - a.sortValue
      }

      return a.sortValue - b.sortValue
    })
  }, [filteredUsers, filteredChats])

  const isInitialLoading = isPostsFetching || chatsFetching

  // Calculate grid columns on resize
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const updateColumns = () => {
      const width = container.offsetWidth
      // Approximate calculation: container width / (item + gap)
      const columns = Math.floor(width / 190) // 140px item + ~50px gap
      setGridColumns(Math.max(1, Math.min(columns, 6)))
    }

    const resizeObserver = new ResizeObserver(updateColumns)
    resizeObserver.observe(container)
    updateColumns()

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  // After loading is complete, show skeletons for an additional second to complete all calculations
  useEffect(() => {
    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    // If loading is complete, start a timer for 1 second
    if (!isInitialLoading) {
      setShowSkeletonsExtra(true)
      timeoutRef.current = setTimeout(() => {
        setShowSkeletonsExtra(false)
      }, SKELETONS_EXTRA_DELAY)
    } else {
      // If loading started again, hide the additional delay
      setShowSkeletonsExtra(false)
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [isInitialLoading])

  useEffect(() => {
    if (!isStepCompleted(STEPS.AI_FEED) && locationPathname === keys.ROUTE_NAMES.NEWS_FEED) {
      setTimeout(() => {
        const event = new CustomEvent('openSetupModal', {
          detail: { stepId: STEPS.AI_FEED, stepTitle: 'AI Feed' },
        })
        window.dispatchEvent(event)
      }, 500)
    }
  }, [isCurrentStepCompleted, isStepCompleted, locationPathname, uid])

  const isLoading =
    (!isCurrentStepCompleted && isSetupAllowedForUser(uid)) ||
    ((appPosts.length === 0 || appChats.length === 0) && (isInitialLoading || showSkeletonsExtra))

  const selectedUserData = selectedUserId ? aggregatedByUser[selectedUserId] : null
  const selectedUserColorIndex = useMemo(() => {
    if (!selectedUserId) return 0
    return sortedItems.findIndex((item) => item.type === 'user' && item.userId === selectedUserId)
  }, [selectedUserId, sortedItems])

  // Split items into waiting and done groups
  const { waitingItems, doneItems } = useMemo(() => {
    const waiting: typeof sortedItems = []
    const done: typeof sortedItems = []

    sortedItems.forEach((item) => {
      if (item.category === 6) {
        done.push(item)
      } else {
        waiting.push(item)
      }
    })

    return { waitingItems: waiting, doneItems: done }
  }, [sortedItems])

  const handleItemClick = useCallback((userId: string) => {
    setSelectedUserId(userId)
  }, [])

  const handleCloseModal = useCallback(() => {
    setSelectedUserId(null)
  }, [])

  const drawer = useMemo(
    () => (
      <PersonalViewDrawer
        isOpen={!!selectedUserId && !!selectedUserData}
        onClose={handleCloseModal}
        userId={selectedUserId || ''}
        tasks={[...(selectedUserData?.overdueTasks || []), ...(selectedUserData?.urgentTasks || [])]}
        uncompletedTasks={selectedUserData?.uncompletedTasks || []}
        completedTasks={selectedUserData?.completedTasks || []}
        posts={selectedUserData?.posts || []}
        readPosts={selectedUserData?.readPosts || []}
        ownerTasks={selectedUserData?.ownerTasks || []}
        isSelf={selectedUserId === uid}
        colorIndex={selectedUserColorIndex}
      />
    ),
    [selectedUserId, selectedUserData, handleCloseModal, selectedUserColorIndex, uid]
  )

  return (
    <Flex position="relative" style={{ columnGap: 0 }} flexDirection={isRtl ? 'row-reverse' : 'row'}>
      {isRtl && drawer}

      <MainContentWrapper ref={scrollContainerRef} px={{ base: 8, md: '30px', lg: '70px' }}>
        {isLoading ? (
          <Grid
            width="100%"
            mb="40px"
            data-flip-active="false"
            css={css`
              display: grid;
              grid-template-columns: repeat(${gridColumns}, minmax(100px, 180px));
              grid-auto-rows: min-content;
              gap: 50px;

              @media (min-width: 900px) {
                gap: 40px;
              }
            `}
          >
            <PersonalViewSkeletons count={6} type="item" />
            <PersonalViewSkeletons count={8} type="chat" />
          </Grid>
        ) : (
          <>
            {/* Waiting section */}
            {(waitingItems.length > 0 || todoTasksCount > 0) && (
              <>
                <Box mb="50px" mx="25px">
                  <SectionHeader title={t_waiting} color={colors.greyDark} fontSize="14px" fontWeight="bold" />
                </Box>
                <Flipper
                  flipKey={`waiting-${waitingItems.length}-${gridColumns}-${!!selectedUserId}-${todoTasksCount}`}
                  spring={{
                    stiffness: 400,
                    damping: 40,
                  }}
                >
                  <Grid
                    width="100%"
                    mb={doneItems.length > 0 ? '40px' : '40px'}
                    css={css`
                      display: grid;
                      grid-template-columns: repeat(${gridColumns}, minmax(100px, 180px));
                      grid-auto-rows: min-content;
                      gap: 50px;

                      @media (min-width: 900px) {
                        gap: 40px;
                      }
                    `}
                  >
                    {/* StoreeDoll at first position when todoTasksCount > 0 */}
                    {/* {(todoTasksCount > 0 || todoTasksCount === 0) && (
                      <Flipped key="storee-doll-waiting" flipId="storee-doll" scale={false}>
                        <Box>
                          <StoreeDollPreview todoTasksCount={todoTasksCount} isPersonalView={true} colorIndex={0} />
                        </Box>
                      </Flipped>
                    )} */}
                    {waitingItems.map((item, index) => {
                      const adjustedIndex = todoTasksCount > 0 ? index + 1 : index
                      const flipId = item.type === 'user' ? item.userId : item.chat.post_id || `chat-${index}`
                      const itemContent =
                        item.type === 'user' ? (
                          <PersonalViewItem
                            userId={item.userId}
                            tasks={[
                              ...aggregatedByUser[item.userId].overdueTasks,
                              ...aggregatedByUser[item.userId].urgentTasks,
                            ]}
                            posts={aggregatedByUser[item.userId].posts}
                            onClick={() => handleItemClick(item.userId)}
                            isFaded={item.isFaded}
                            colorIndex={adjustedIndex}
                          />
                        ) : (
                          <ChatPreview
                            chat={item.chat}
                            isPersonalView={true}
                            isFaded={item.isFaded}
                            colorIndex={adjustedIndex}
                          />
                        )
                      return (
                        <Flipped key={flipId} flipId={flipId} scale={false}>
                          <Box>{itemContent}</Box>
                        </Flipped>
                      )
                    })}
                  </Grid>
                </Flipper>
              </>
            )}

            {/* Done section */}
            {(doneItems.length > 0 || todoTasksCount === 0) && (
              <>
                <Box mb="50px" mt={waitingItems.length > 0 || todoTasksCount > 0 ? '50px' : '0'} mx="25px">
                  <SectionHeader title={t_done} color={colors.greyDark} fontSize="14px" fontWeight="bold" />
                </Box>
                <Flipper
                  flipKey={`done-${doneItems.length}-${gridColumns}-${!!selectedUserId}-${todoTasksCount}`}
                  spring={{
                    stiffness: 400,
                    damping: 40,
                  }}
                >
                  <Grid
                    width="100%"
                    mb="40px"
                    css={css`
                      display: grid;
                      grid-template-columns: repeat(${gridColumns}, minmax(100px, 180px));
                      grid-auto-rows: min-content;
                      gap: 50px;

                      @media (min-width: 900px) {
                        gap: 40px;
                      }
                    `}
                  >
                    {/* StoreeDoll at first position when todoTasksCount === 0 (with fade effect) */}
                    {/* {todoTasksCount === 0 && (
                      <Flipped key="storee-doll-done" flipId="storee-doll" scale={false}>
                        <Box
                          css={css`
                            grid-column-start: 1;
                          `}
                        >
                          <StoreeDollPreview todoTasksCount={0} isPersonalView={true} colorIndex={0} />
                        </Box>
                      </Flipped>
                    )} */}
                    {doneItems.map((item, index) => {
                      const adjustedIndex = todoTasksCount === 0 ? index + 1 : index
                      const isFirstDoneItem = index === 0 && todoTasksCount > 0
                      const flipId =
                        item.type === 'user'
                          ? item.userId
                          : item.chat.post_id || `chat-done-${waitingItems.length + index}`
                      const baseColorIndex = todoTasksCount > 0 ? waitingItems.length + 1 : waitingItems.length
                      const itemContent =
                        item.type === 'user' ? (
                          <PersonalViewItem
                            userId={item.userId}
                            tasks={[
                              ...aggregatedByUser[item.userId].overdueTasks,
                              ...aggregatedByUser[item.userId].urgentTasks,
                            ]}
                            posts={aggregatedByUser[item.userId].posts}
                            onClick={() => handleItemClick(item.userId)}
                            isFaded={item.isFaded}
                            colorIndex={baseColorIndex + adjustedIndex}
                          />
                        ) : (
                          <ChatPreview
                            chat={item.chat}
                            isPersonalView={true}
                            isFaded={item.isFaded}
                            colorIndex={baseColorIndex + adjustedIndex}
                          />
                        )

                      return (
                        <Flipped key={flipId} flipId={flipId} scale={false}>
                          <Box
                            css={css`
                              ${isFirstDoneItem ? 'grid-column-start: 1;' : ''}
                            `}
                          >
                            {itemContent}
                          </Box>
                        </Flipped>
                      )
                    })}
                  </Grid>
                </Flipper>
              </>
            )}

            {chatsFetching && !allChatsLoaded && (
              <Grid
                width="100%"
                mb="40px"
                data-flip-active="false"
                css={css`
                  display: grid;
                  grid-template-columns: repeat(${gridColumns}, minmax(100px, 180px));
                  grid-auto-rows: min-content;
                  gap: 50px;

                  @media (min-width: 900px) {
                    gap: 40px;
                  }
                `}
              >
                <PersonalViewSkeletons count={4} type="chat" />
              </Grid>
            )}
          </>
        )}
      </MainContentWrapper>

      {!isRtl && drawer}
    </Flex>
  )
}

export default PersonalView
